import apiService from './apiService';

/**
 * Service for fetching customer-facing promotional banners.
 * Connects to the public /api/ecommerce/banners endpoint.
 */
class BannerService {
    /**
     * Normalize raw banner data from backend to ensure consistent structure
     * across different field naming conventions (camelCase vs snake_case).
     * @param {object} item - Raw banner object
     * @returns {object} Normalized banner object
     */
    normalizeBanner(item) {
        if (!item || typeof item !== 'object') return null;

        const rawOrder = item.display_order ?? item.displayOrder ?? item.order;
        const displayOrder = Number.isFinite(Number(rawOrder)) ? Number(rawOrder) : 0;

        const rawStatus = (item.status || '').toLowerCase();
        const isActive =
            item.is_active !== false &&
            item.isActive !== false &&
            rawStatus !== 'inactive' &&
            rawStatus !== 'draft';

        const extractMediaValue = (val) => {
            if (!val) return '';
            if (typeof val === 'string') return val.trim();
            if (typeof val === 'object') {
                return String(val.url || val.secure_url || val.path || val.src || '').trim();
            }
            return '';
        };

        const rawDesktop =
            extractMediaValue(item.desktop_image) ||
            extractMediaValue(item.desktopImage) ||
            extractMediaValue(item.banner_image) ||
            extractMediaValue(item.bannerImage) ||
            extractMediaValue(item.image_url) ||
            extractMediaValue(item.imageUrl) ||
            extractMediaValue(item.image) ||
            extractMediaValue(item.url);

        const rawMobile =
            extractMediaValue(item.mobile_image) ||
            extractMediaValue(item.mobileImage) ||
            extractMediaValue(item.mobile_banner) ||
            extractMediaValue(item.mobileBanner) ||
            extractMediaValue(item.mobile_image_url) ||
            extractMediaValue(item.mobileImageUrl) ||
            extractMediaValue(item.image_mobile);

        const rawFit = String(
            item.image_fit || item.imageFit || item.object_fit || item.objectFit || 'contain'
        )
            .trim()
            .toLowerCase();
        const imageFit = rawFit === 'cover' ? 'cover' : 'contain';

        const focalPoint =
            String(
                item.focal_point ||
                    item.focalPoint ||
                    item.object_position ||
                    item.objectPosition ||
                    'center'
            ).trim() || 'center';

        return {
            _id: String(item._id || item.id || `banner-${Math.random()}`),
            title: item.title || '',
            subtitle: item.subtitle || item.description || '',
            desktop_image: rawDesktop || rawMobile || '',
            mobile_image: rawMobile || null,
            image_fit: imageFit,
            focal_point: focalPoint,
            cta_text: item.cta_text || item.ctaText || item.button_text || item.buttonText || '',
            cta_url: item.cta_url || item.ctaUrl || item.button_url || item.buttonUrl || item.link || '',
            display_order: displayOrder,
            is_active: isActive,
            start_date: item.start_date || item.startDate || null,
            end_date: item.end_date || item.endDate || null,
            alt_text: item.alt_text || item.altText || item.title || 'Rahber Corporation Special Promotion',
        };
    }

    /**
     * Check if a banner is currently within its scheduled active window.
     * @param {object} banner - Normalized banner object
     * @returns {boolean} True if eligible for display
     */
    isCurrentlyActive(banner) {
        if (!banner || !banner.is_active) return false;
        if (!banner.desktop_image && !banner.mobile_image) return false;

        const now = new Date();

        if (banner.start_date) {
            const startDate = new Date(banner.start_date);
            if (!isNaN(startDate.getTime()) && startDate > now) {
                return false;
            }
        }

        if (banner.end_date) {
            const endDate = new Date(banner.end_date);
            if (!isNaN(endDate.getTime()) && endDate < now) {
                return false;
            }
        }

        return true;
    }

    /**
     * Fetch all published and currently active banners for the public frontend.
     * Respects display order and scheduled dates.
     * @returns {Promise<Array>} Array of active banners, sorted by display_order
     */
    async getActiveBanners() {
        try {
            // Primary standard endpoint for ecommerce banners
            const response = await apiService.get('/ecommerce/banners');

            let rawList = [];
            if (response && response.data) {
                if (Array.isArray(response.data.banners)) {
                    rawList = response.data.banners;
                } else if (Array.isArray(response.data)) {
                    rawList = response.data;
                }
            } else if (Array.isArray(response)) {
                rawList = response;
            }

            if (!Array.isArray(rawList) || rawList.length === 0) {
                return [];
            }

            const activeList = rawList
                .map((item) => this.normalizeBanner(item))
                .filter((banner) => this.isCurrentlyActive(banner))
                .sort((a, b) => a.display_order - b.display_order);

            return activeList;
        } catch (error) {
            return [];
        }
    }

    /**
     * Default banners fallback when none are available.
     * Returns an empty array so no static or placeholder banners are shown.
     */
    getDefaultBanners() {
        return [];
    }
}

const bannerService = new BannerService();
export default bannerService;
